import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Drawer, List, ListItemIcon, ListItemText, Box, Divider, ListSubheader, Avatar, Card, CardHeader, Skeleton, Chip, ListItemButton, Collapse, IconButton, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { SlowMotionVideoOutlined as SlowMotionVideoOutlinedIcon, CorporateFare as CorporateFareIcon, ReorderOutlined, PeopleAltOutlined, LogoutOutlined, ExpandLess, ExpandMore, Settings as SettingsIcon, EmojiEvents, Sports, Scoreboard, MonitorHeart, Person, Stadium, Group, Groups, SportsSoccer } from "@mui/icons-material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkRemoveIcon from "@mui/icons-material/BookmarkRemove";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SportsIcon from '@mui/icons-material/Sports';
import FunctionsIcon from '@mui/icons-material/Functions';
import {
  useBookmarkedFixtures,
  formatKickoffCountdown,
  getKickoffDate,
  getFixturePhase,
} from "../../utils/bookmarkedFixtures";

import packageJson from "../../../package.json";
import AetherLogo from "../../styles/AetherLogo.png";
import TimelineIcon from '@mui/icons-material/Timeline';
import { useSession } from "../../contexts/SessionContext";
import { isSuperAdmin } from "../../utils/appAccess";
import { getStoredSession } from "../../utils/sessionManager";
import { useUI } from "../../contexts/UIContext";
import authService from "../../services/auth.service";
import sessionService from "../../services/session.service";
const drawerWidth = 260;

// Single bookmark row — extracted so we can reuse it inside the per-week
// accordion. Visuals match the original flat-list card to keep operator
// muscle memory intact.
function BookmarkRow({ b, now, onRemove, onOpen }) {
  const kickoff = getKickoffDate(b);
  const countdown = formatKickoffCountdown(b, now);
  const phase = getFixturePhase(b, now);
  const chipLabel = phase === "live" ? "Live" : phase === "finished" ? "FT" : countdown;
  const chipBg =
    phase === "live"
      ? "rgba(239,68,68,0.18)"
      : phase === "finished"
      ? "rgba(74,222,128,0.14)"
      : "rgba(69,255,213,0.15)";
  const chipColor =
    phase === "live" ? "#fca5a5" : phase === "finished" ? "#86efac" : "#45FFD5";
  const kickoffLabel = kickoff
    ? kickoff.toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
  const homeShort =
    b.home_team_short_name ||
    (b.fixture_name?.split(/\s+vs?\s+/i)?.[0] ?? "HOME");
  const awayShort =
    b.away_team_short_name ||
    (b.fixture_name?.split(/\s+vs?\s+/i)?.[1] ?? "AWAY");

  return (
    <Box
      role="button"
      onClick={() => onOpen(b)}
      sx={{
        position: "relative",
        p: 1,
        pl: 1,
        pr: 4.5,
        borderRadius: 1.5,
        cursor: "pointer",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
        transition: "all 120ms ease",
        "&:hover": {
          background:
            "linear-gradient(180deg, rgba(69,255,213,0.10) 0%, rgba(69,255,213,0.04) 100%)",
          borderColor: "rgba(69,255,213,0.25)",
        },
      }}
    >
      <Tooltip title="Remove from watchlist" arrow placement="left">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(b.fixture_id);
          }}
          sx={{
            position: "absolute",
            top: "50%",
            right: 6,
            transform: "translateY(-50%)",
            p: 0.5,
            color: "rgba(255,255,255,0.85)",
            "& .bm-remove-hover": { display: "none" },
            "& .bm-default": { display: "inline-flex" },
            "&:hover": {
              color: "#ef4444",
              bgcolor: "rgba(239,68,68,0.12)",
              "& .bm-default": { display: "none" },
              "& .bm-remove-hover": { display: "inline-flex" },
            },
          }}
        >
          <BookmarkIcon className="bm-default" sx={{ fontSize: 18 }} />
          <BookmarkRemoveIcon className="bm-remove-hover" sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mb: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {b.home_team_logo ? (
            <Box
              component="img"
              src={b.home_team_logo}
              alt=""
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              sx={{
                width: 18, height: 18, objectFit: "contain", flexShrink: 0,
                filter:
                  "drop-shadow(0 0 1px rgba(255,255,255,0.4)) drop-shadow(0 0 2px rgba(255,255,255,0.15))",
              }}
            />
          ) : (
            <Box sx={{ width: 18, height: 18, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.92)",
              flex: 1, minWidth: 0, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.2,
            }}
          >
            {homeShort}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, my: 0.125 }}>
          <Box sx={{ width: 18, flexShrink: 0, display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                textTransform: "uppercase", color: "rgba(255,255,255,0.35)", lineHeight: 1,
              }}
            >
              vs
            </Typography>
          </Box>
          <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(255,255,255,0.08)" }} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {b.away_team_logo ? (
            <Box
              component="img"
              src={b.away_team_logo}
              alt=""
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              sx={{
                width: 18, height: 18, objectFit: "contain", flexShrink: 0,
                filter:
                  "drop-shadow(0 0 1px rgba(255,255,255,0.4)) drop-shadow(0 0 2px rgba(255,255,255,0.15))",
              }}
            />
          ) : (
            <Box sx={{ width: 18, height: 18, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.92)",
              flex: 1, minWidth: 0, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.2,
            }}
          >
            {awayShort}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography
          sx={{
            fontSize: 10.5, color: "rgba(255,255,255,0.55)", flex: 1, minWidth: 0,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {kickoffLabel}
        </Typography>
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            height: 18, fontSize: 10, fontWeight: 700,
            bgcolor: chipBg, color: chipColor,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Box>
    </Box>
  );
}

function BookmarkedFixturesSection() {
  const navigate = useNavigate();
  const { bookmarks, groupedByWeek, removeBookmark, removeBulk } = useBookmarkedFixtures();
  const [now, setNow] = useState(() => Date.now());
  // Which week accordions are currently expanded. Defaults to "first week" so
  // the operator sees something immediately when the section appears.
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set());
  const [clearAllOpen, setClearAllOpen] = useState(false);

  // Initialise expansion: open the first week that contains a live fixture,
  // else the first week in the list. Only do this once per bookmarks shape.
  useEffect(() => {
    if (expandedWeeks.size > 0 || groupedByWeek.length === 0) return;
    const liveGroup = groupedByWeek.find((g) =>
      g.items.some((b) => getFixturePhase(b, Date.now()) === "live"),
    );
    setExpandedWeeks(new Set([(liveGroup ?? groupedByWeek[0]).week]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByWeek.length]);

  useEffect(() => {
    if (bookmarks.length === 0) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [bookmarks.length]);

  const toggleWeek = (week) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(week)) next.delete(week);
      else next.add(week);
      return next;
    });
  };

  const openBookmark = (b) => {
    if (!b.competition_id || !b.season_id) return;
    navigate(`/teamsetup/${b.competition_id}/${b.season_id}/${b.fixture_id}`);
  };

  if (bookmarks.length === 0) return null;

  return (
    <>
      <Divider sx={{ mx: 2, mt: 2, mb: 0.5 }} />
      <Box
        sx={{
          mt: 1.5,
          mb: 1,
          mx: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <BookmarkIcon sx={{ fontSize: "1.1em", color: "rgba(255,255,255,0.85)" }} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.6)",
            flex: 1,
          }}
        >
          My Watchlist
        </Typography>
        {/* <Tooltip title="Open full bookmarks page" arrow>
          <IconButton
            size="small"
            onClick={() => navigate("/my-watchlist")}
            sx={{
              p: 0.25,
              color: "rgba(255,255,255,0.55)",
              "&:hover": { color: "#45FFD5", bgcolor: "rgba(69,255,213,0.10)" },
            }}
          >
            <OpenInFullIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip> */}
        <Tooltip title="Remove all bookmarks" arrow>
          <IconButton
            size="small"
            onClick={() => setClearAllOpen(true)}
            sx={{
              p: 0.25,
              color: "rgba(255,255,255,0.55)",
              "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.12)" },
            }}
          >
            <DeleteSweepIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 1, pb: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {groupedByWeek.map((group) => {
          const isOpen = expandedWeeks.has(group.week);
          const liveCount = group.items.reduce(
            (n, b) => n + (getFixturePhase(b, now) === "live" ? 1 : 0),
            0,
          );
          return (
            <Box key={group.week} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box
                role="button"
                onClick={() => toggleWeek(group.week)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  pl: 0.5,
                  pr: 0.875,
                  py: 0.625,
                  borderRadius: 1,
                  cursor: "pointer",
                  bgcolor: "transparent",
                  transition: "background-color 100ms ease",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.04)",
                  },
                }}
              >
                {isOpen ? (
                  <ExpandLess sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
                )}
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.65)",
                    flex: 1,
                  }}
                >
                  {group.week === "—" ? "Other" : `Week ${group.week}`}
                </Typography>
                {liveCount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.375,
                      px: 0.625,
                      py: "1px",
                      borderRadius: 8,
                      bgcolor: "rgba(239,68,68,0.16)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#ef4444",
                        boxShadow: "0 0 6px rgba(239,68,68,0.7)",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: "#fca5a5",
                        lineHeight: 1.2,
                      }}
                    >
                      {liveCount}
                    </Typography>
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.45)",
                    minWidth: 14,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {group.items.length}
                </Typography>
              </Box>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 0.25 }}>
                  {group.items.map((b) => (
                    <BookmarkRow
                      key={b.fixture_id}
                      b={b}
                      now={now}
                      onRemove={removeBookmark}
                      onOpen={openBookmark}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      <Dialog
        open={clearAllOpen}
        onClose={() => setClearAllOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1E1E1E",
            borderRadius: 3,
            border: "1px solid rgba(255,255,255,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#FFFFFF", fontSize: 18, fontWeight: 600, pb: 1 }}>
          Clear your watchlist?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            This removes all {bookmarks.length} fixture{bookmarks.length === 1 ? "" : "s"} from your
            watchlist. This action can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearAllOpen(false)}
            sx={{ color: "rgba(255,255,255,0.85)", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              removeBulk(bookmarks.map((b) => b.fixture_id));
              setClearAllOpen(false);
            }}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#dc2626" },
            }}
          >
            Remove all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function LeftDrawer() {
  const location = useLocation();
  const { username, session } = useSession();
  // Custom Stats is super-admin only — gate the nav item on the wildcard '*'
  // permission. Fall back to the stored session if the context hasn't hydrated.
  const superAdmin = isSuperAdmin(session ?? getStoredSession());
  const { isDrawerOpen } = useUI();
  const serviceVersion = packageJson.serviceVersion || packageJson.version || "";
  const envMatch = serviceVersion.match(/-([^.]+)/);
  const environment = envMatch ? envMatch[1] : (serviceVersion.includes('-') ? serviceVersion.split('-')[1] : 'prod');
  const environmentLabel = environment ? environment.charAt(0).toUpperCase() + environment.slice(1) : 'Prod';
  const [isLoading, setIsLoading] = useState(true);
  const [openLogoutMenu, setOpenLogoutMenu] = useState(false);
  const handleToggle = () => {
    setOpenLogoutMenu(!openLogoutMenu);
  };
  const handleLogout = async () => {
    await sessionService.deleteSession();
    authService.clearSession();
    window.location.href = '/';
  };

  const baseMenu = [
    {
      path: "/competitions",
      icon: <EmojiEvents />,
      text: "Competitions",
      subHeader: <Divider sx={{ mx: 2, mb: 2 }}></Divider>,
    },
    {
      path: "/sports",
      icon: <Sports />,
      text: "Sports",
      subHeader: <Divider sx={{ mx: 2, mb: 2 }}></Divider>,
    },
    {
      path: "/collections/teams",
      icon: <Groups />,
      text: "Teams",
      subHeader: <Divider sx={{ mx: 2, mb: 2 }}></Divider>,
      matchPrefix: ["/collections/teams"],
    },
    {
      path: "/collections/players",
      icon: <Person />,
      text: "Players",
      matchPrefix: ["/collections/players"],
    },
    {
      path: "/collections/staff",
      icon: <Group />,
      text: "Staff",
      matchPrefix: ["/collections/staff"],
    },
    {
      path: "/collections/officials",
      icon: <SportsIcon />,
      text: "Officials",
      matchPrefix: ["/collections/officials"],
    },
    {
      path: "/collections/venues",
      icon: <Stadium />,
      text: "Venues",
      matchPrefix: ["/collections/venues"],
    },
    {
      path: "/formations",
      icon: <SportsSoccer />,
      text: "Formations",
      matchPrefix: ["/formations"],
    },
    {
      // OPUS-1408. Top-level rather than a season tab: competition and season
      // are chosen on the page, so it is reachable without drilling in first.
      path: "/all-fixtures",
      icon: <Scoreboard />,
      text: "All Fixtures",
      matchPrefix: ["/all-fixtures"],
    },
    {
      path: "/custom-stats",
      icon: <FunctionsIcon />,
      text: "Custom Stats",
      matchPrefix: ["/custom-stats"],
      superAdmin: true,
    },
  ];
  // Hide super-admin-only items from everyone else.
  const menu = baseMenu.filter((item) => !item.superAdmin || superAdmin)
  return (
    <Drawer
      anchor="left"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: 0.5,
          pt: 0,
          m: "0.5em",
          borderRadius: "0.5em",
          height: "calc(100% - 1em)",
          borderRight: "none",
        },
      }}
      variant="persistent"
      open={isDrawerOpen}
    >
      <div>
        <Box sx={{ textAlign: "center", mt: 2, mb: 1 }}>
          <Link to="/competitions" style={{ textDecoration: "none" }}>
            <img src={AetherLogo} alt="Logo" style={{ width: "250px", opacity: '100%', cursor: 'pointer' }} />
          </Link>
        </Box>

        <List sx={{ pt: 0.5, mt: 0 }}>
          {menu.map((item, index) => {
            const selected = item.matchPrefix
              ? item.matchPrefix.some((p) => location.pathname.startsWith(p))
              : location.pathname === item.path;
            return (
              <React.Fragment key={index}>
                {item?.subHeader}
                <Link to={item.path} style={{ display: "inherit", textDecoration: "none", color: "white" }}>
                  <ListItemButton
                    selected={selected}
                    sx={{
                      mt: 0.5,
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: selected ? "#00000035" : "none",
                      borderRadius: 1,
                      p: 0,
                      pl: 2,
                      mx: 0.5
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        fontSize: "1.3em",
                        minWidth: "unset",
                        mr: 2,
                      }}
                    >
                      {selected && (
                        <Divider
                          orientation={"vertical"}
                          color={"white"}
                          sx={{
                            borderRadius: "1.3em",
                            backgroundColor: "#45FFD5",
                            height: "1.4em",
                            width: "3px",
                            position: "absolute",
                            left: "-0.5em",
                            top: "0",
                            boxShadow: "0 0 10px #45FFD5",
                          }}
                        />
                      )}
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      slotProps={{
                        primary: { style: { fontSize: "1.3em", fontWeight: selected ? "bold" : "" } },
                      }}
                    />
                  </ListItemButton>
                </Link>
              </React.Fragment>
            );
          })}
        </List>

        <BookmarkedFixturesSection />

      </div>

      <Box sx={{ textAlign: "center" }}>
        <Divider sx={{ mx: 2 }}></Divider>

        <Card elevation={0} sx={{ textAlign: "left" }}>
          {!isLoading ? (                    //set loading to false
            <CardHeader
              avatar={<Skeleton variant="rounded" width={40} height={40} />}
              title={<Skeleton width={120} />}
              subheader={<Skeleton width={80} />}
            />
          ) : (
            <>
              <Collapse in={openLogoutMenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ mt: 0 }}>
                  <Link style={{ display: "inherit", textDecoration: "none", color: "white" }} onClick={handleLogout}>
                    <ListItemButton
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        alignItems: "center",
                        borderRadius: 1,
                        p: 0,
                        pl: 2,
                        mx: 0.5
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                          fontSize: "1.3em",
                          minWidth: "unset",
                          mr: 2,
                        }}
                      >
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText
                        primary={'Logout'}
                        slotProps={{
                          primary: { style: { fontSize: "1.3em" } },
                        }}
                      />
                    </ListItemButton>
                  </Link>
                </List>
              </Collapse>

              <CardHeader
                avatar={
                  (
                    <Avatar sx={{}} aria-label="recipe" variant="rounded">
                      {/* {userInfo?.data?.organization_id?.name?.charAt(0).toUpperCase() || "O"} */}

                    </Avatar>
                  )
                }
                // title={userInfo?.data?.name || "User"}
                // subheader={userInfo?.data?.organization_id?.name || "ORG"}
                title={username || "Aelive_User"}
                // subheader={"ORG"}
                action={
                  <IconButton onClick={handleToggle}>
                    {openLogoutMenu ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
              />
            </>
          )}
        </Card>

        <Divider sx={{ mx: 2, mb: 2 }}></Divider>

        <Box sx={{ textAlign: 'center', p: 0, m: 0, mb: 1, mt: 1.5, opacity: '33%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
          <Chip color="error" size="small" label={environmentLabel} sx={{ mr: 1 }} />
          {/* v{packageJson.version} */}
          v{packageJson.serviceVersion}
        </Box>
      </Box>
    </Drawer>
  );
}